import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import MainLayout from '../../components/layout/MainLayout';
import HondaLoader from '../../components/common/HondaLoader';
import AccordionSection from '../../components/common/AccordionSection';
import { getCessionInformation } from '../../services/cesionesService';

// ─── Sub-componentes estables (fuera del componente principal) ───

const Fila = ({ label, value }) => (
  <div className="flex justify-between py-1 border-b border-gray-100 last:border-b-0">
    <span className="text-xs text-gray-500 flex-shrink-0 mr-2">{label}</span>
    <span className="text-xs font-medium text-gray-900 text-right">{value || '—'}</span>
  </div>
);

const getEstadoBadge = (estado) => {
  const e = (estado || '').toUpperCase();
  if (e.includes('APROBADO')) return { color: 'bg-green-100 text-green-700 border-green-300', icon: 'check_circle' };
  if (e.includes('RECHAZADO')) return { color: 'bg-red-100 text-red-700 border-red-300', icon: 'cancel' };
  if (e.includes('REVISION')) return { color: 'bg-yellow-100 text-yellow-700 border-yellow-300', icon: 'pending' };
  if (e.includes('PENDIENTE')) return { color: 'bg-blue-100 text-blue-700 border-blue-300', icon: 'schedule' };
  return { color: 'bg-gray-100 text-gray-700 border-gray-300', icon: 'info' };
};

const buildDocUrl = (doc) => {
  if (!doc) return null;
  if (doc.documentPath) return doc.documentPath;
  if (doc.documentUrl) return doc.documentUrl;
  if (!doc.documentB64) return null;
  const lower = (doc.documentName || '').toLowerCase();
  const mime = lower.endsWith('.pdf') ? 'application/pdf'
    : lower.endsWith('.png') ? 'image/png'
    : lower.endsWith('.jpg') || lower.endsWith('.jpeg') ? 'image/jpeg'
    : 'application/octet-stream';
  return `data:${mime};base64,${doc.documentB64}`;
};

// ─── Componente principal ───

const VisualizacionCesion = ({ user, onLogout, onNavigate }) => {
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [cesion, setCesion] = useState(null);
  const [error, setError] = useState(null);
  const [docActual, setDocActual] = useState(0);

  useEffect(() => {
    const navData = location.state;
    if (!navData || (!navData.cessionID && !navData.grupo)) {
      setError('No se recibieron datos de la cesion para visualizar.');
      setLoading(false);
      return;
    }

    const loadData = async () => {
      try {
        setLoading(true);
        const data = await getCessionInformation({
          group: navData.grupo || '',
          order: navData.orden || '',
          cessionID: navData.cessionID || '',
          sellerID : user.ce_ID || 0
        });

        if (!data || !data.objContract) {
          setError('No se encontraron datos para esta cesion.');
          return;
        }

        setCesion({
          cessionID: data.cessionID,
          grupo: data.objContract.group,
          orden: data.objContract.order,
          tipoFormulario: data.objContract.formType,
          personType: data.personType || data.objBuyer?.personType || '',
          estado: data.cessionStatus || navData.estado || '',
          statusNotes: data.statusNotes || data.notes || '',
          comprador: {
            nombre: data.objBuyer?.fullName,
            dni: data.objBuyer?.ident,
            telefono: data.objBuyer?.phone,
            email: data.objBuyer?.email,
            tipo: data.objBuyer?.personType,
          },
          vendedor: {
            nombre: data.objSeller?.fullName,
            dni: data.objSeller?.ident,
            telefono: data.objSeller?.phone,
            email: data.objSeller?.email,
            tipo: data.objSeller?.personType,
          },
          documentos: data.documents || [],
        });
      } catch (err) {
        setError(err.message || 'No se pudo cargar la informacion de la cesion.');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [location.state]);

  const handleAnterior = useCallback(() => setDocActual(i => Math.max(0, i - 1)), []);
  const handleSiguiente = useCallback((max) => setDocActual(i => Math.min(max - 1, i + 1)), []);

  if (loading) {
    return (
      <MainLayout user={user} onLogout={onLogout} onNavigate={onNavigate}>
        <div className="flex items-center justify-center h-screen">
          <HondaLoader message="Cargando datos de la cesion..." />
        </div>
      </MainLayout>
    );
  }

  if (error || !cesion) {
    return (
      <MainLayout user={user} onLogout={onLogout} onNavigate={onNavigate}>
        <div className="max-w-screen-xl mx-auto px-6 py-6">
          <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-8 text-center">
            <span className="material-icons text-5xl text-red-400 block mb-3">error_outline</span>
            <p className="text-red-700 font-semibold">{error || 'No se encontraron datos'}</p>
            <button onClick={() => onNavigate('back')} className="mt-4 px-5 py-2 border-2 border-gray-200 text-gray-600 rounded-xl text-sm font-medium hover:bg-gray-50 transition-colors flex items-center gap-2 mx-auto">
              <span className="material-icons text-sm">arrow_back</span>Volver
            </button>
          </div>
        </div>
      </MainLayout>
    );
  }

  const estadoBadge = getEstadoBadge(cesion.estado);
  const isPersonaJuridica = cesion.personType?.includes('2') || cesion.comprador?.tipo?.includes('2');
  const documentos = cesion.documentos || [];
  const docIndex = Math.min(docActual, Math.max(0, documentos.length - 1));
  const docUrl = buildDocUrl(documentos[docIndex]);

  return (
    <MainLayout user={user} onLogout={onLogout} onNavigate={onNavigate}>
      <div className="max-w-full mx-auto px-4 py-4">

        {/* Titulo */}
        <div className="mb-4">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-1.5 h-8 gradient-red rounded-full shadow-glow-red"></div>
            <h2 className="text-2xl font-bold text-gradient">Visualizacion de Cesion</h2>
          </div>
          <p className="text-gray-500 text-sm ml-5">Detalle completo del expediente de cesion</p>
        </div>

        {/* Layout: izquierda (docs) + derecha (info) */}
        <div className="flex flex-col lg:flex-row gap-4">

          {/* ── COLUMNA IZQUIERDA: Visor de documentos ── */}
          <div className="flex-1 bg-white border-2 border-gray-800 rounded-xl shadow-lg overflow-hidden flex flex-col">
            <div className="h-2 bg-gray-800"></div>
            <div className="bg-gray-800 px-3 py-2 border-b border-gray-700">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
                <div className="flex items-center gap-4">
                  <p className="text-xs text-white">
                    <span className="font-bold">Documento:</span> {documentos[docIndex]?.documentName || 'Sin documento'}
                  </p>
                  <p className="text-xs text-white">
                    <span className="font-bold">Anexo:</span> {documentos.length > 0 ? `${docIndex + 1} / ${documentos.length}` : '0 / 0'}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button onClick={handleAnterior} disabled={docIndex === 0 || documentos.length === 0}
                    className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded-md transition text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                  >
                    <span className="material-icons text-sm">chevron_left</span>Anterior
                  </button>
                  <button onClick={() => handleSiguiente(documentos.length)} disabled={docIndex === documentos.length - 1 || documentos.length === 0}
                    className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded-md transition text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                  >
                    Siguiente<span className="material-icons text-sm">chevron_right</span>
                  </button>
                </div>
              </div>
            </div>
            <div className="flex-1 p-3 flex items-center justify-center bg-gray-100" style={{ minHeight: '550px' }}>
              {documentos.length === 0 ? (
                <div className="text-center">
                  <span className="material-icons text-5xl text-gray-400 mb-2 block">description</span>
                  <p className="text-sm text-gray-500">No hay documentos disponibles</p>
                  <p className="text-xs text-gray-400 mt-1">Este expediente no tiene documentos adjuntos</p>
                </div>
              ) : docUrl ? (
                <iframe src={docUrl} title={documentos[docIndex]?.documentName} className="w-full h-full rounded-lg border border-gray-300" style={{ minHeight: '520px' }} />
              ) : (
                <div className="text-center">
                  <span className="material-icons text-5xl text-gray-400 mb-2 block">pending</span>
                  <p className="text-sm text-gray-500">Documento pendiente de carga</p>
                  <p className="text-xs text-gray-400 mt-1">{documentos[docIndex]?.documentName}</p>
                </div>
              )}
            </div>
          </div>

          {/* ── COLUMNA DERECHA: Datos ── */}
          <div className="w-full lg:w-[35%] bg-white border-2 border-gray-800 rounded-xl shadow-lg overflow-hidden flex flex-col">
            <div className="h-2 bg-gray-800"></div>
            <div className="px-3 py-2 border-b border-gray-700 bg-gray-800 flex items-center">
              <p className="text-xs font-bold text-white truncate">Cesion ID: {cesion.cessionID || 'N/A'}</p>
            </div>

            <div className="flex-1 overflow-y-auto" style={{ maxHeight: '550px' }}>

              {/* Motivo rechazo */}
              {cesion.estado?.toUpperCase().includes('RECHAZADO') && cesion.statusNotes && (
                <div className="mx-3 mt-3 bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-xs font-bold text-red-800 mb-1">Motivo de rechazo</p>
                  <p className="text-xs text-red-700">{cesion.statusNotes}</p>
                </div>
              )}

              <AccordionSection title="DATOS DEL CONTRATO" icon="description">
                <Fila label="Cession ID" value={cesion.cessionID} />
                <Fila label="Grupo" value={cesion.grupo} />
                <Fila label="Orden" value={cesion.orden} />
                <Fila label="Tipo de Formulario" value={cesion.tipoFormulario} />
                <Fila label="Tipo de Persona" value={cesion.personType} />
              </AccordionSection>

              <AccordionSection title={isPersonaJuridica ? 'COMPRADORA (EMPRESA)' : 'COMPRADOR'} icon={isPersonaJuridica ? 'business' : 'person_add'}>
                <Fila label="Nombre / Razon Social" value={cesion.comprador.nombre} />
                <Fila label="DNI / CUIT" value={cesion.comprador.dni} />
                <Fila label="Telefono" value={cesion.comprador.telefono} />
                <Fila label="Email" value={cesion.comprador.email} />
                {cesion.comprador.tipo && <Fila label="Tipo de Persona" value={cesion.comprador.tipo} />}
              </AccordionSection>

              <AccordionSection title="VENDEDOR (CEDENTE)" icon="person_remove">
                <Fila label="Nombre / Razon Social" value={cesion.vendedor.nombre} />
                <Fila label="DNI / CUIT" value={cesion.vendedor.dni} />
                <Fila label="Telefono" value={cesion.vendedor.telefono} />
                <Fila label="Email" value={cesion.vendedor.email} />
                {cesion.vendedor.tipo && <Fila label="Tipo de Persona" value={cesion.vendedor.tipo} />}
              </AccordionSection>


            </div>


          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default VisualizacionCesion;